import React from 'react';
import { Card, CardHeader, CardContent, Typography, Box, ImageList, ImageListItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PhotoCamera as PhotoIcon } from '@mui/icons-material';
import type { Photo } from '../../../../types';

interface PhotosPreviewProps {
  photos: Photo[];
  totalCount: number;  // ✅ CRITICAL: Always a number (0 or greater), never undefined
  personId: string;
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

/**
 * PhotosPreview - Latest 6 photos
 *
 * ✅ CRITICAL FIX: Ensures photo count badge always shows
 * The totalCount is initialized to 0 in PersonPage state (not undefined)
 * This fixes the badge display issue in ProfileSubnav
 */
export const PhotosPreview: React.FC<PhotosPreviewProps> = ({
  photos,
  totalCount,
  personId,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const navigate = useNavigate();

  const handleViewAllPhotos = () => {
    navigate(`/people/${personId}/photos`);
  };

  const handlePhotoClick = (photoId: string) => {
    // TODO: Open photo modal/lightbox
    console.log('View photo:', photoId);
  };

  const displayPhotos = photos.slice(0, 6);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Photos</Typography>
            {totalCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                ({totalCount})
              </Typography>
            )}
          </Box>
        }
        action={
          totalCount > 0 && (
            <Typography
              component="a"
              variant="caption"
              sx={{
                cursor: 'pointer',
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={handleViewAllPhotos}
            >
              View All →
            </Typography>
          )
        }
      />

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {displayPhotos.length === 0 ? (
          /* Empty state */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4
            }}
          >
            <PhotoIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
              No photos yet. Upload photos to preserve memories.
            </Typography>
          </Box>
        ) : (
          /* Photos grid */
          <ImageList
            cols={3}
            gap={8}
            sx={{
              width: '100%',
              m: 0
            }}
          >
            {displayPhotos.map((photo) => (
              <ImageListItem
                key={photo.id}
                onClick={() => handlePhotoClick(photo.id)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 1,
                  overflow: 'hidden',
                  '&:hover': {
                    opacity: 0.8,
                    transform: 'scale(1.02)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <img
                  src={photo.thumbnailURL || photo.storageURL}
                  alt={photo.caption || 'Photo'}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    aspectRatio: '1'
                  }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </CardContent>
    </Card>
  );
};
